import axios from 'axios';

export const API_BASE_URL = process.env.NODE_ENV === 'staging' || process.env.NODE_ENV === 'development'
  ? 'http://localhost:3001'
  : 'https://api.karvlo.com';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Types for beta signup
export interface BetaSignupData {
  firstName: string;
  lastName: string;
  email: string;
  areaCode: string;
  phone: string;
  userType: 'driver' | 'rider';
  vehicleBodyType?: string;
}

export interface BetaSignupResponse {
  success: boolean;
  message: string;
  userId?: string;
}

export interface NewsletterSignupData {
  email: string;
}

// Types for sign in
export interface SignInData {
  email: string;
  password: string;
  userType: 'driver' | 'rider';
}

export interface SignInResponse {
  success: boolean;
  message: string;
  requiresTwoFactor?: boolean;
  pendingToken?: string;
  data?: {
    user: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      userType: 'driver' | 'rider';
      accountStatus: string;
      emailVerified: boolean;
      lastLogin: string;
      signupDate?: string;
    };
    tokens: {
      accessToken: string;
      refreshToken: string;
    };
  };
}

// API Functions
export const betaSignup = async (data: BetaSignupData): Promise<BetaSignupResponse> => {
  try {
    const response = await api.post('/api/beta-signup', data);
    return response.data;
  } catch (error: any) {
    // Re-throw original so callers can read error.response.status / .data
    throw error;
  }
};

export const newsletterSignup = async (data: NewsletterSignupData): Promise<BetaSignupResponse> => {
  try {
    const response = await api.post('/api/newsletter-signup', data);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Newsletter signup failed');
  }
};

export const getBetaStats = async () => {
  try {
    const response = await api.get('/api/beta-stats');
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch stats');
  }
};

export const signIn = async (email: string, password: string, userType: 'driver' | 'rider'): Promise<SignInResponse> => {
  try {
    const response = await api.post('/api/login', { email, password, userType });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Sign in failed');
  }
};

export const complete2FALogin = async (pendingToken: string, totpCode: string): Promise<SignInResponse> => {
  try {
    const response = await api.post('/api/2fa/complete-login', { pendingToken, token: totpCode });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || '2FA verification failed');
  }
};

export default api;
